import { computed, DestroyRef, effect, inject, Injectable, Provider, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, map, Observable, timeout } from 'rxjs';
import { info, warn } from '@4players/odin-common';
import { UserSettingsStore } from '../../state/user-settings.repository';
import { TAppLanguage } from '../../shared/schemas/settings';
import { isAppLanguage, resolveBrowserLanguage } from '../../shared/i18n/languages';
import { DATE_LOCALE, resolveLocale } from '../../shared/i18n/date-format';

const DEFAULT_LANGUAGE: TAppLanguage = 'en';
// Startup waits this long for the translations at most; a slower file still
// applies when it arrives.
const READY_TIMEOUT_MS = 5_000;

function readBrowserLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  if (navigator.languages?.length) return navigator.languages;
  return navigator.language ? [navigator.language] : [];
}

/**
 * Owns the interface language: the user's stored choice, or the browser's
 * preferred languages while there is none ("Automatic"). Applies it to
 * ngx-translate and `<html lang>`, and switches live when either changes.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  readonly #translate = inject(TranslateService);
  readonly #userSettings = inject(UserSettingsStore);
  readonly #document = inject(DOCUMENT);

  readonly #browserLanguages = signal(readBrowserLanguages());

  /** The browser's first preferred language the app supports; English when none is. */
  readonly browserLanguage = computed(
    () => resolveBrowserLanguage(this.#browserLanguages()) ?? DEFAULT_LANGUAGE
  );

  /** The user's choice; `null` follows the browser. */
  readonly preference = toSignal(
    this.#userSettings.language$.pipe(
      map((language) => (isAppLanguage(language) ? language : null))
    ),
    { requireSync: true }
  );

  /** The interface language: the user's choice, else the browser's. */
  readonly language = computed(() => this.preference() ?? this.browserLanguage());

  /**
   * The language whose translations are in effect. Unlike `language`, it only
   * changes once the file has loaded, and it notifies on every switch. Read it
   * in a `computed()` before calling `instant()` so the result is re-translated.
   */
  readonly active = toSignal(this.#translate.onLangChange.pipe(map((event) => event.lang)), {
    initialValue:
      this.#translate.getCurrentLang() ?? this.#translate.getFallbackLang() ?? DEFAULT_LANGUAGE,
    equal: () => false,
  });

  /**
   * The `Intl` locale for dates and times in the active language, with the
   * region of the browser's preferred locale in that language (`de-AT`).
   */
  readonly locale = computed(() => resolveLocale(this.active(), this.#browserLanguages()));

  // The language last handed to ngx-translate, so the effect doesn't repeat it.
  #requested: TAppLanguage | null = null;

  constructor() {
    effect(() => {
      const language = this.language();
      if (language !== this.#requested) this.#use(language);
    });
    effect(() => {
      this.#document.documentElement.lang = this.active();
    });

    const onLanguageChange = () => this.#browserLanguages.set(readBrowserLanguages());
    window.addEventListener('languagechange', onLanguageChange);
    inject(DestroyRef).onDestroy(() =>
      window.removeEventListener('languagechange', onLanguageChange)
    );
  }

  /** Stores the user's choice; `null` returns to following the browser. */
  setPreference(language: TAppLanguage | null): void {
    this.#userSettings.store.update((state) =>
      state.language === language ? state : { ...state, language }
    );
  }

  /**
   * Requests the interface language and resolves once its translations are in
   * effect. Awaited by an app initializer so views that translate during
   * construction don't render English first. Never rejects: on a failed or
   * slow load the app starts anyway with the English keys.
   */
  async ready(): Promise<void> {
    const language = this.language();
    try {
      await firstValueFrom(this.#use(language).pipe(timeout(READY_TIMEOUT_MS)));
    } catch (e) {
      warn(`language ${language} is not ready; starting with ${DEFAULT_LANGUAGE}`, { e });
    }
  }

  #use(language: TAppLanguage): Observable<unknown> {
    info(`use language ${language}`);
    this.#requested = language;
    return this.#translate.use(language);
  }
}

/** Provides `DATE_LOCALE`, the date locale for the shared layer, from `LanguageService`. */
export function provideDateLocale(): Provider {
  return { provide: DATE_LOCALE, useFactory: () => inject(LanguageService).locale };
}
